import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radar, ArrowUpRight, Library, Signal } from "lucide-react";
import { Header } from "@/components/Header";
import { ProbeSequence } from "@/components/ProbeSequence";
import { PlanetOrb } from "@/components/PlanetOrb";
import { RarityBadge, HazardMeter } from "@/components/Badges";
import { useDiscover } from "@/hooks/useDiscover";
import { api, hashPos, brightColor } from "@/lib/api";

const RARITY_SIZE = { Common: 10, Rare: 13, Legendary: 17, Mythic: 22 };

const Stat = ({ label, value, testid }) => (
  <div className="glass rounded-lg p-4" data-testid={testid}>
    <p className="label-mono">{label}</p>
    <p className="font-display text-2xl font-bold text-slate-50 mt-1">{value ?? "—"}</p>
  </div>
);

export default function Universe() {
  const [planets, setPlanets] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const { job, start, dismiss } = useDiscover();

  const load = () => {
    api.get("/planets/mine").then((r) => { setPlanets(r.data); setSelected((s) => s || r.data[0] || null); }).catch(() => {});
    api.get("/stats/me").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(load, []);
  useEffect(() => { if (!job) load(); }, [job]);

  const nodes = useMemo(() => planets.map((p) => ({
    ...p, x: 8 + hashPos(p.id) * 84, y: 10 + hashPos(p.id, 7) * 76, size: RARITY_SIZE[p.rarity] || 10, color: brightColor(p.palette),
  })), [planets]);

  return (
    <div className="relative z-10 min-h-screen" data-testid="universe-page">
      <Header />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10 grid lg:grid-cols-12 gap-6">
        <section className="lg:col-span-8 glass hud-corners scanlines relative rounded-2xl grid-bg min-h-[62vh] overflow-hidden" data-testid="galaxy-map">
          <div className="absolute left-5 top-5 z-10">
            <p className="label-mono">Sector map · {planets.length} charted</p>
          </div>
          <div className="absolute right-5 top-5 z-10 flex gap-2">
            {["Common", "Rare", "Legendary", "Mythic"].map((r) => (
              <span key={r} className="label-mono !text-[0.55rem] flex items-center gap-1">
                <span className="inline-block rounded-full bg-cyan-300" style={{ width: RARITY_SIZE[r] / 2, height: RARITY_SIZE[r] / 2 }} /> {r}
              </span>
            ))}
          </div>

          <svg className="absolute inset-0 h-full w-full pointer-events-none">
            {nodes.slice(1).map((n, i) => (
              <line key={n.id} x1={`${nodes[i].x}%`} y1={`${nodes[i].y}%`} x2={`${n.x}%`} y2={`${n.y}%`} stroke="rgba(0,240,255,0.14)" strokeDasharray="3 6" />
            ))}
          </svg>

          {nodes.map((n, i) => (
            <motion.button key={n.id} onClick={() => setSelected(n)} data-testid="galaxy-node"
              initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.03, type: "spring" }}
              className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{ left: `${n.x}%`, top: `${n.y}%` }} title={n.name}>
              <span className={`block rounded-full ${selected?.id === n.id ? "animate-pulse" : ""}`}
                style={{ width: n.size, height: n.size, background: n.color, boxShadow: `0 0 ${n.size}px ${n.color}` }} />
              {selected?.id === n.id && <span className="absolute inset-[-10px] rounded-full border border-cyan-300/60 animate-pulse-ring" />}
              <span className="absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap font-mono text-[0.6rem] uppercase tracking-[0.2em] text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity">
                {n.name}
              </span>
            </motion.button>
          ))}

          {planets.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="galaxy-empty-state">
              <Radar className="h-10 w-10 text-cyan-400/70 mb-4" />
              <p className="font-display text-lg font-semibold uppercase tracking-wide text-slate-200">No worlds charted yet</p>
              <p className="text-sm text-slate-400 mt-2 max-w-sm">Your sector is dark. Launch a probe to discover your first planet.</p>
            </div>
          )}

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
            <button onClick={start} disabled={!!job || stats?.probes_left === 0} className="btn-primary !px-10 !py-4 relative" data-testid="discover-planet-button">
              <span className="absolute inset-0 rounded-full border border-cyan-300/60 animate-pulse-ring" />
              <Radar className="h-4 w-4" /> Discover new planet
            </button>
            {stats && <p className="label-mono text-center mt-3" data-testid="probes-left">{stats.probes_left} probes left today</p>}
          </div>
        </section>

        <aside className="lg:col-span-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Discovered" value={stats?.discovered} testid="stat-discovered" />
            <Stat label="Saved" value={stats?.saved} testid="stat-saved" />
            <Stat label="Legendary+" value={stats?.legendary} testid="stat-legendary" />
            <Stat label="Shared" value={stats?.shared} testid="stat-shared" />
          </div>

          {selected ? (
            <motion.div key={selected.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass hud-corners rounded-xl p-6" data-testid="selected-planet-panel">
              <p className="label-mono mb-4">Target lock</p>
              <div className="flex gap-4 items-center">
                <div className="w-24 shrink-0"><PlanetOrb planet={selected} glow={false} /></div>
                <div className="min-w-0">
                  <p className="font-mono text-[0.62rem] tracking-[0.25em] text-cyan-400 uppercase">{selected.star_code}</p>
                  <h3 className="font-display text-xl font-semibold text-slate-50 truncate" data-testid="selected-planet-name">{selected.name}</h3>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{selected.tagline}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3">
                <RarityBadge rarity={selected.rarity} />
                <HazardMeter level={selected.hazard_level} compact />
              </div>
              <Link to={`/planet/${selected.id}`} className="btn-ghost w-full mt-5" data-testid="enter-selected-planet-button">
                Enter world <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          ) : (
            <div className="glass rounded-xl p-6 text-sm text-slate-400" data-testid="no-selection-panel">Select a node on the map to lock a target.</div>
          )}

          <div className="glass rounded-xl p-5">
            <p className="label-mono mb-3">Recent discoveries</p>
            <ul className="space-y-2" data-testid="recent-discoveries">
              {planets.slice(0, 5).map((p) => (
                <li key={p.id}>
                  <Link to={`/planet/${p.id}`} className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-cyan-500/10 transition-colors" data-testid="recent-discovery-item">
                    <span className="font-mono text-xs text-slate-200 truncate">{p.name}</span>
                    <span className="label-mono !text-[0.55rem]">{p.rarity}</span>
                  </Link>
                </li>
              ))}
              {planets.length === 0 && <li className="text-xs text-slate-500">Nothing logged yet.</li>}
            </ul>
            <div className="flex gap-2 mt-4">
              <Link to="/library" className="btn-ghost flex-1 !px-3" data-testid="go-library-button"><Library className="h-3.5 w-3.5" /> Library</Link>
              <Link to="/feed" className="btn-ghost flex-1 !px-3" data-testid="go-feed-button"><Signal className="h-3.5 w-3.5" /> Signals</Link>
            </div>
          </div>
        </aside>
      </main>

      <ProbeSequence job={job} onDismiss={dismiss} onRetry={() => { dismiss(); start(); }} />
    </div>
  );
}
